import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowRightOutlined,
  CloudServerOutlined,
  FileProtectOutlined,
  LoginOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Header from "../../components/Header/LandingHeader";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import LandingFooter from "../../components/LandingFooter/LandingFooter";
import CorridorMap from "../../components/CorridorMap/CorridorMap";
import useWindowDimensions from "../../components/WindowDimentions/WindowDimentions";
import { getEvents, getNews } from "../../../../redux/LangingPageSlice";
import s from "./Home.module.scss";
import NewsItem from "../../components/NewsItem/NewsItem";
import CountUp from "../../components/CountUp/CountUp";

const facts = [
  {
    value: "4,256 km",
    label: "Length of the Trans-Caspian route",
    countDuration: 1500,
  },
  {
    value: "508 km",
    label: "Sea routes across the Caspian",
    countDuration: 2000,
    countStep: 20,
  },
  { value: "10 countries", label: "Represented on the Board" },
  { value: "PPP · B2B · B2G", label: "Partnership models we work with" },
];

const hubSteps = [
  {
    icon: <FileProtectOutlined />,
    title: "Documents in advance",
    text: "All documents for customs clearance and logistics are prepared before the vehicle arrives in the destination country.",
  },
  {
    icon: <TeamOutlined />,
    title: "Regional partners",
    text: "Representatives in each country administer the HUB and serve customers from other partner countries.",
  },
  {
    icon: <CloudServerOutlined />,
    title: "Transit marketplace",
    text: "One platform consolidating the capabilities of regional specialists to provide comprehensive transit services.",
  },
];

const organisations = [
  { name: "European Union", logo: "/images/partners/eu.svg" },
  { name: "United Nations", logo: "/images/partners/un.svg" },
  { name: "World Customs Organization", logo: "/images/partners/wco.svg" },
  {
    name: "Organization for Security and Co-operation in Europe",
    logo: "/images/partners/osce.svg",
    logoWidth: "80%",
  },
  { name: "GIZ", logo: "/images/partners/giz.svg" },
  { name: "Asian Development Bank", logo: "/images/partners/adb.svg" },
];
const programmes = [
  {
    short: "TITR",
    name: "Trans-Caspian International Transport Route (TITR)",
    logo: "/images/partners/titr.svg",
    logoHeight: 66,
  },
  {
    short: "TRACECA",
    name: "Europe–Caucasus–Asia Transport Corridor",
    logo: "/images/partners/traceca.png",
    logoHeight: 64,
  },
  {
    short: "CAREC",
    name: "China–Central Asia–Europe program",
    logo: "/images/partners/carec.svg",
    logoHeight: 58,
  },
  { short: "Silk Road", name: "Digitalization of the Silk Road" },
];

const countries = [
  { name: "Azerbaijan", flag: "/images/azerbaijan.png" },
  { name: "Georgia", flag: "/images/georgia.png" },
  { name: "Kazakhstan", flag: "/images/kazakhstan.png" },
  { name: "Kyrgyzstan", flag: "/images/kyrgyzstan.png" },
  { name: "Moldova", flag: "/images/moldova.png" },
  { name: "Tajikistan", flag: "/images/tajikistan.png" },
  { name: "Turkey", flag: "/images/turkey.png" },
  { name: "Turkmenistan", flag: "/images/turkmenistan.png" },
  { name: "Ukraine", flag: "/images/ukraine.png" },
  { name: "Uzbekistan", flag: "/images/uzbekistan.png" },
];

const SectionHead = ({ eyebrow, title, link, linkText }) => (
  <div className={s.section_head}>
    <div>
      <div className={s.eyebrow}>{eyebrow}</div>
      <h2 className={s.h2}>{title}</h2>
    </div>
    {link && (
      <Link to={link} className={`${s.more_link} ${s.head_link}`}>
        {linkText} <ArrowRightOutlined />
      </Link>
    )}
  </div>
);

const Home = () => {
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const news = useSelector((state) => state.landing.news);

  useEffect(() => {
    dispatch(getNews());
    dispatch(getEvents());
  }, [dispatch]);

  const latestNews = Array.isArray(news) ? news.slice(0, 3) : [];

  return (
    <div className={s.home}>
      {width > 700 ? <Header /> : <MobileHeader />}

      {/* Hero */}
      <section className={s.hero}>
        <div className={s.hero_map}>
          <CorridorMap />
        </div>
        <div className={s.hero_overlay}></div>
        <div className={s.hero_content}>
          <div className={s.hero_eyebrow}>ANV</div>
          <h1 className={s.hero_title}>
            Middle Corridor Transit Advisory Committee
          </h1>
          <p className={s.hero_text}>
            Customs and logistics experts from Europe and the Middle Corridor
            countries, working for the fast movement of goods and vehicles
            across our borders.
          </p>
          <div className={s.hero_actions}>
            <Link to="/news" className={s.btn_primary}>
              Upcoming Events <ArrowRightOutlined />
            </Link>
            <Link to="/committee" className={s.btn_outline}>
              <UserOutlined /> Advisory Committee
            </Link>
          </div>
        </div>
      </section>

      <section className={s.facts}>
        {facts.map((f) => (
          <div className={s.fact} key={f.label}>
            <div className={s.fact_value}>
              <CountUp
                value={f.value}
                duration={f.countDuration}
                step={f.countStep}
              />
            </div>
            <div className={s.fact_label}>{f.label}</div>
          </div>
        ))}
      </section>

      <section className={s.section}>
        <div className={s.about}>
          <div className={s.about_text}>
            <div className={s.eyebrow}>
              UPCOMING EVENTS — EVERYONE IS WELCOME!
            </div>
            <h2 className={s.h2}>
              1st Regional Forum and Workshop on the Middle Corridor
            </h2>
            <p>
              The National Customs Association of Moldova and its affiliated
              body, the Middle Corridor Transit Advisory Committee (MCTAC), in
              cooperation with the German Agency for International Cooperation
              (GIZ), are planning to organize a series of Meetings and Workshops
              for MCTAC Partners in Europe, the Caspian Region, and Central Asia
              on the digitalization of transit procedures along the Middle
              Corridor."
            </p>
            <p>
              The main objective of these events is to present the Digital
              Transit HUB, a digital tool developed by the MCTAC team to
              facilitate and streamline transit procedures along the Middle
              Corridor.
            </p>
            <div className={s.about_links}>
              <Link to="/news/upcoming-events" className={s.more_link}>
                Read more <ArrowRightOutlined />
              </Link>
            </div>
          </div>
          <div className={s.about_media}>
            <img
              src="/images/mld.png"
              alt="MCTAC international conference"
            />
          </div>
        </div>
      </section>

      {/* Regional representatives */}
      <section className={s.regions}>
        <div className={s.inner}>
          <div className={s.regions_grid}>
            <div>
              <div className={s.eyebrow}>Regional representatives</div>
              <h2 className={s.h2}>Partners in every country of the route</h2>
              <p className={s.lead}>
                Regional representatives organise customs clearance for goods
                and vehicles, oversee logistics operations and protect the
                rights of carriers and entrepreneurs in their regions.
              </p>
              <Link to="/regionalRepresentatives" className={s.btn_dark}>
                Find a representative <ArrowRightOutlined />
              </Link>
            </div>
            <div className={s.flags}>
              {countries.map((c) => (
                <Link
                  to="/regionalRepresentatives"
                  className={s.flag_item}
                  key={c.name}
                >
                  <img src={c.flag} alt="" />
                  <span>{c.name}</span>
                  <ArrowRightOutlined className={s.flag_arrow} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      {latestNews.length > 0 && (
        <section className={`${s.section} ${s.news_section}`}>
          <div className={s.inner}>
            <SectionHead
              eyebrow="Concluded events"
              title="News & events"
              link="/news"
              linkText="All news"
            />
            <div className={s.news}>
              {latestNews.map((n) => (
                <NewsItem key={n.id} news={n} />
              ))}
            </div>
            {/* mobile only: the header link moves below the news */}
            <Link to="/news" className={`${s.more_link} ${s.news_more}`}>
              All news <ArrowRightOutlined />
            </Link>
          </div>
        </section>
      )}

      {/* Digital Transit HUB */}
      <section className={s.hub}>
        <div className={s.inner}>
          <div className={s.hub_grid}>
            <div className={s.hub_intro}>
              <div className={s.eyebrow_light}>Flagship project</div>
              <h2 className={s.h2_light}>Digital Transit HUB</h2>
              <p>
                Developed at the request of MCTAC within the framework of the
                TITR programme, the HUB simplifies trade and transit procedures
                along the Middle Corridor and enables expedited vehicle
                crossings at border checkpoints.
              </p>
              <div className={s.hub_actions}>
                <Link to="/signIn" className={s.btn_primary}>
                  <LoginOutlined /> Enter the HUB
                </Link>
                <Link to="/aboutHub" className={s.btn_outline}>
                  Learn more
                </Link>
              </div>
            </div>
            <div className={s.hub_steps}>
              {hubSteps.map((step) => (
                <div className={s.hub_step} key={step.title}>
                  <div className={s.hub_icon}>{step.icon}</div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className={s.section}>
        <div className={s.inner}>
          <div className={s.partners_head}>
            <div className={s.eyebrow}>Partners</div>
            <h2 className={s.h2}>
              MCTAC works closely with regional and global organizations
            </h2>
          </div>
          <div className={s.logos}>
            {organisations.map((o) => (
              <div className={s.logo_card} key={o.name}>
                <div className={s.logo_img}>
                  <img
                    src={o.logo}
                    alt={o.name}
                    style={{ maxWidth: o.logoWidth }}
                  />
                </div>
                <div className={s.logo_name}>{o.name}</div>
              </div>
            ))}
          </div>
          <div className={s.programmes_title}>
            Regional cooperation programs
          </div>
          <div className={s.programmes}>
            {programmes.map((p) => (
              <div className={s.programme} key={p.short}>
                {/* top row: the logo, or the title for programmes without one */}
                {p.logo ? (
                  <div className={s.programme_logo}>
                    <img
                      src={p.logo}
                      alt={p.short}
                      style={{ height: p.logoHeight }}
                    />
                  </div>
                ) : (
                  <div className={`${s.programme_short} ${s.programme_head}`}>
                    {p.short}
                  </div>
                )}
                <div className={s.programme_name}>{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Home;
